/**
 * AI Condition Grader modal — seller-only.
 *
 * Flow:
 *   1. Seller picks/snaps a card photo.
 *   2. We POST it to /api/streams/{stream_id}/grade (multipart).
 *   3. Backend GPT-5.2 vision returns a predicted PSA grade + subgrades.
 *   4. Seller previews the result (chat viewers don't see it yet).
 *   5. Seller hits "Broadcast" → POST .../grade/{grade_id}/broadcast
 *      The grade is pushed to chat as a special "grade" message visible
 *      to all viewers, and the listing gets a predicted-grade badge.
 *
 * Disclaimer pinned at the bottom: AI estimate, not a certified PSA grade.
 */
import { useRef, useState } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ImageSquareIcon, XIcon, BroadcastIcon, SparkleIcon } from "@phosphor-icons/react";

const SubgradeBar = ({ label, value }) => (
  <div className="flex items-center gap-2 text-xs">
    <div className="w-20 text-white/70 capitalize">{label}</div>
    <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-amber-300 to-amber-500"
        style={{ width: `${(value / 10) * 100}%` }}
      />
    </div>
    <div className="w-10 text-right font-mono font-bold text-white">{value.toFixed(1)}</div>
  </div>
);

export const GraderModal = ({ open, onClose, streamId, activeListing }) => {
  const [phase, setPhase] = useState("pick"); // pick | grading | preview | broadcasting
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setPhase("pick");
    setPreview(null);
    setFile(null);
    setResult(null);
  };

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) {
      toast.error("Use a JPEG, PNG, or WEBP photo.");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submitGrade = async () => {
    if (!file) return;
    setPhase("grading");
    const fd = new FormData();
    fd.append("file", file);
    if (activeListing?.id) fd.append("listing_id", activeListing.id);
    if (activeListing?.title) fd.append("extra_context", activeListing.title);
    try {
      const { data } = await api.post(`/streams/${streamId}/grade`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      });
      setResult(data);
      setPhase("preview");
    } catch (err) {
      console.error("grade_failed", err);
      const detail = err?.response?.data?.detail || "Grading failed — try a clearer photo.";
      toast.error(typeof detail === "string" ? detail : "Grading failed");
      setPhase("pick");
    }
  };

  const broadcast = async () => {
    if (!result?.grade_id) return;
    setPhase("broadcasting");
    try {
      await api.post(`/streams/${streamId}/grade/${result.grade_id}/broadcast`);
      toast.success(`Broadcast! Predicted PSA ${result.grade}`);
      onClose();
      reset();
    } catch (err) {
      console.error("broadcast_failed", err);
      toast.error("Broadcast failed");
      setPhase("preview");
    }
  };

  const close = () => {
    if (phase === "grading" || phase === "broadcasting") return;
    onClose();
    setTimeout(reset, 200);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent
        className="max-w-md bg-zinc-950 border-white/10 text-white p-0 overflow-hidden"
        data-testid="grader-modal"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/10">
          <DialogTitle className="flex items-center gap-2 text-base font-black">
            <SparkleIcon size={18} weight="fill" className="text-amber-300" />
            AI Condition Grader
            <span className="ml-auto text-[10px] font-bold text-amber-300/80 uppercase tracking-wider">
              TCG · Beta
            </span>
          </DialogTitle>
        </DialogHeader>

        {phase === "pick" && (
          <div className="p-5 space-y-4">
            {/* LEGAL DISCLAIMER — shown every time the seller opens the
                AI Condition Grader. Linked to the Terms section so the
                seller can review the full language.  This is the
                primary acknowledgement gate; broadcasting an AI grade
                additionally surfaces a confirm step (see "preview"
                phase). */}
            <div
              className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-3 py-2.5 text-[11px] leading-snug text-amber-100"
              data-testid="grader-disclaimer-banner"
            >
              <p className="font-black tracking-wide text-amber-200 uppercase mb-1">
                AI estimate &mdash; not a certified grade
              </p>
              <p>
                This is an experimental AI estimate from a single photo.
                It is <span className="font-bold">not</span> affiliated
                with PSA, BGS, SGC, CGC, or any third-party grading or
                authentication service, and is not authentication,
                appraisal, investment, or legal advice. Always recommend
                buyers obtain professional third-party grading.{" "}
                <a
                  href="/terms#ai-grading"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold hover:text-white"
                  data-testid="grader-disclaimer-terms-link"
                >
                  Read full disclaimer &amp; terms &rarr;
                </a>
              </p>
            </div>

            <p className="text-xs text-white/70 leading-relaxed">
              Take a clear, well-lit photo of the card. Centering, corners, edges, and surface should
              all be visible. Out-of-sleeve only — sleeves cause grading errors.
            </p>

            {preview ? (
              <div className="relative">
                <img
                  src={preview}
                  alt="card preview"
                  className="w-full rounded-xl border border-white/15 object-contain max-h-72 bg-zinc-900"
                />
                <button
                  onClick={() => {
                    setPreview(null);
                    setFile(null);
                  }}
                  className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/70 hover:bg-black flex items-center justify-center"
                  data-testid="grader-clear-photo"
                >
                  <XIcon size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="w-full h-44 rounded-xl border-2 border-dashed border-white/20 hover:border-amber-300/60 hover:bg-white/5 flex flex-col items-center justify-center gap-2 transition"
                data-testid="grader-pick-photo"
              >
                <ImageSquareIcon size={32} className="text-white/40" />
                <span className="text-sm font-semibold text-white/70">Pick or snap a card photo</span>
                <span className="text-[10px] text-white/40 uppercase tracking-wider">JPEG · PNG · WEBP · max 6 MB</span>
              </button>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="hidden"
              onChange={handlePick}
              data-testid="grader-file-input"
            />

            <Button
              onClick={submitGrade}
              disabled={!file}
              className="w-full rounded-full h-11 bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-zinc-950 font-black"
              data-testid="grader-submit-btn"
            >
              <SparkleIcon size={16} weight="fill" className="mr-1.5" />
              Grade this card
            </Button>
          </div>
        )}

        {phase === "grading" && (
          <div className="p-8 flex flex-col items-center gap-3 text-center">
            <div className="h-10 w-10 border-2 border-amber-300 border-t-transparent rounded-full animate-spin" />
            <div className="text-sm font-bold">Analyzing centering, corners, edges, surface…</div>
            <div className="text-[11px] text-white/50">Usually 5–10 seconds</div>
          </div>
        )}

        {phase === "preview" && result && (
          <div className="p-5 space-y-4">
            <div className="rounded-2xl bg-gradient-to-br from-amber-500/15 via-amber-400/10 to-transparent border border-amber-300/30 p-4">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300/80">
                    Predicted PSA
                  </div>
                  <div
                    className="text-6xl font-black font-mono leading-none mt-1"
                    data-testid="grader-result-grade"
                  >
                    {result.grade.toFixed(result.grade % 1 === 0 ? 0 : 1)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-white/50">
                    Confidence
                  </div>
                  <div className="text-2xl font-black mt-1">{result.confidence}%</div>
                </div>
              </div>
              {result.card_name && (
                <div className="text-xs text-white/80 mt-2 font-semibold">{result.card_name}</div>
              )}
            </div>

            <div className="space-y-2">
              <SubgradeBar label="Centering" value={result.subgrades.centering} />
              <SubgradeBar label="Corners" value={result.subgrades.corners} />
              <SubgradeBar label="Edges" value={result.subgrades.edges} />
              <SubgradeBar label="Surface" value={result.subgrades.surface} />
            </div>

            <div className="text-[12px] text-white/70 leading-relaxed bg-white/5 rounded-xl p-3">
              {result.reasoning}
            </div>

            {result.red_flags?.length > 0 && (
              <div className="text-[11px] text-rose-300 leading-relaxed bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 space-y-1">
                <div className="font-bold uppercase tracking-wider">Red flags</div>
                {result.red_flags.map((f, i) => (
                  <div key={i}>• {f}</div>
                ))}
              </div>
            )}

            <div
              className="text-[11px] text-amber-200 leading-snug rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2"
              data-testid="grader-broadcast-disclaimer"
            >
              <span className="font-bold">AI estimate &mdash; not a certified grade.</span>{" "}
              Not affiliated with PSA, BGS, SGC, or CGC. Not authentication
              or appraisal. By broadcasting, you confirm the photo is of
              your item and agree to the{" "}
              <a
                href="/terms#ai-grading"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-semibold hover:text-white"
              >
                AI grading terms
              </a>.
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setPhase("pick");
                  setResult(null);
                }}
                variant="outline"
                className="flex-1 rounded-full h-11 bg-transparent border-white/20 hover:bg-white/10 text-white"
                data-testid="grader-retry-btn"
              >
                Retry
              </Button>
              <Button
                onClick={broadcast}
                className="flex-[2] rounded-full h-11 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-black"
                data-testid="grader-broadcast-btn"
              >
                <BroadcastIcon size={16} weight="fill" className="mr-1.5" />
                Broadcast to viewers
              </Button>
            </div>
          </div>
        )}

        {phase === "broadcasting" && (
          <div className="p-8 flex flex-col items-center gap-3 text-center">
            <div className="h-10 w-10 border-2 border-amber-300 border-t-transparent rounded-full animate-spin" />
            <div className="text-sm font-bold">Broadcasting to chat…</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** Compact in-chat card for a broadcast grade prediction. */
export const GradeChatMessage = ({ message }) => {
  const p = message.grade_payload || {};
  const grade = Number(p.grade || 0);
  return (
    <div
      className="rounded-xl border border-amber-400/40 bg-gradient-to-br from-amber-500/15 via-amber-400/10 to-transparent px-3 py-2.5 text-white"
      data-testid="chat-grade-card"
    >
      <div className="flex items-center gap-2">
        <SparkleIcon size={14} weight="fill" className="text-amber-300" />
        <span className="text-[10px] uppercase tracking-wider font-black text-amber-300">
          AI Predicted Grade
        </span>
        <span className="ml-auto text-[10px] text-white/55 font-bold">{p.confidence}% conf.</span>
      </div>
      <div className="flex items-baseline gap-2 mt-1.5">
        <div className="text-3xl font-black font-mono leading-none">
          PSA {grade.toFixed(grade % 1 === 0 ? 0 : 1)}
        </div>
        {p.card_name && (
          <div className="text-[11px] text-white/70 font-semibold truncate">{p.card_name}</div>
        )}
      </div>
      {p.reasoning && (
        <div className="text-[11px] text-white/75 mt-1.5 leading-snug line-clamp-3">{p.reasoning}</div>
      )}
      <div className="text-[9px] text-white/55 mt-1.5 italic leading-snug">
        AI estimate &middot; not a certified grade &middot; not affiliated with
        PSA, BGS, SGC, or CGC. Buyers: get pro grading before purchase.
      </div>
    </div>
  );
};

export default GraderModal;
